import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import axios from 'axios';
import { AuthProvider, useAuth } from './context/AuthContext';
import config from './config';
import Navbar from './components/Navbar';
import LoginPage from './pages/LoginPage';
import CoursesPage from './pages/CoursesPage';
import ApplicationForm from './pages/ApplicationForm';
import PaymentPage from './pages/PaymentPage';
import ConfirmationPage from './pages/ConfirmationPage';
import ProfilePage from './pages/ProfilePage';
import ProfileSetupPage from './pages/ProfileSetupPage';
import CancelPage from './pages/CancelPage';
import SupportPage from './pages/SupportPage';
import AdmissionsPage from './pages/AdmissionsPage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import DigitalReceiptPage from './pages/DigitalReceiptPage';
import MaintenancePage from './pages/MaintenancePage';
import WhatsAppButton from './components/WhatsAppButton';
import SupportButton from './components/SupportButton';
import Footer from './components/Footer';
import AdminLayout from './pages/admin/AdminLayout';
import DashboardOverview from './pages/admin/DashboardOverview';
import AdminAdmissions from './pages/admin/AdminAdmissions';
import AdminColleges from './pages/admin/AdminColleges';
import AdminCollegeDetails from './pages/admin/AdminCollegeDetails';
import AdminStudents from './pages/admin/AdminStudents';
import AdminSettings from './pages/admin/AdminSettings';
import AdminUserManagement from './pages/admin/AdminUserManagement';
import AdminSettlements from './pages/admin/AdminSettlements';
import AdminLinkedAccounts from './pages/admin/AdminLinkedAccounts';
import AdminLogs from './pages/admin/AdminLogs';
import AdminProfile from './pages/admin/AdminProfile';
import ErpIntegrationsPage from './pages/admin/ErpIntegrationsPage';
import CourseManagement from './pages/admin/CourseManagement';
import NoPermission from './components/admin/NoPermission';
import { getTextEntryWordLimit, limitWords } from './utils/wordLimits';

import { PERMISSIONS, hasAnyPermission, hasPermission } from './constants/permissions';

const isProfileComplete = (user) => {
  return user && user.name && user.email && user.mobile && user.dob;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{background:'var(--bg)'}}>
      <div className="w-8 h-8 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin"/>
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  if (!isProfileComplete(user)) return <Navigate to="/profile-setup" />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (user.role === 'admin') return <Navigate to="/admin" />;
    if (user.role === 'cluster_admin') return <Navigate to="/cluster-admin" />;
    if (user.role === 'institution_admin') return <Navigate to="/institution" />;
    return <Navigate to="/courses" />;
  }
  return children;
};

const ProfileSetupRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  if (isProfileComplete(user)) {
    if (user.role === 'admin') return <Navigate to="/admin" />;
    if (user.role === 'cluster_admin') return <Navigate to="/cluster-admin" />;
    if (user.role === 'institution_admin') return <Navigate to="/institution" />;
    return <Navigate to="/courses" />;
  }
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.role !== 'admin') return <Navigate to="/login" />;
  return children;
};

const ClusterAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.role !== 'cluster_admin') return <Navigate to="/login" />;
  return children;
};

const InstitutionAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.role !== 'institution_admin') return <Navigate to="/login" />;
  return children;
};

const PermissionRoute = ({ permission, children }) => {
  const { user } = useAuth();
  const allowed = Array.isArray(permission)
    ? hasAnyPermission(user, permission)
    : hasPermission(user, permission);
  if (!allowed) return <NoPermission />;
  return children;
};

const ScrollToTop = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, search.replace(/([?&])q=[^&]*&?/, '$1').replace(/[?&]$/, '')]);

  return null;
};

const ADMISSIONS_FILTER_SESSION_KEY = 'seatifyai.adminAdmissions.filters';
const isAdminAdmissionsPath = (pathname = '') => (
  pathname.startsWith('/admin/admissions')
  || pathname.startsWith('/cluster-admin/admissions')
  || pathname.startsWith('/institution/admissions')
);

const ClearAdmissionsFiltersOnRouteLeave = () => {
  const { pathname } = useLocation();
  const previousPathRef = useRef(pathname);

  useEffect(() => {
    const previousPath = previousPathRef.current;
    if (isAdminAdmissionsPath(previousPath) && !isAdminAdmissionsPath(pathname)) {
      sessionStorage.removeItem(ADMISSIONS_FILTER_SESSION_KEY);
    }
    previousPathRef.current = pathname;
  }, [pathname]);

  return null;
};

const EnforceTextEntryWordLimits = () => {
  useEffect(() => {
    const enforceLimit = (event) => {
      const limit = getTextEntryWordLimit(event.target);
      if (!limit) return;

      const limitedValue = limitWords(event.target.value, limit);
      if (limitedValue === event.target.value) return;

      event.target.value = limitedValue;
    };

    document.addEventListener('input', enforceLimit, true);
    return () => document.removeEventListener('input', enforceLimit, true);
  }, []);

  return null;
};

const AppRoutes = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [backendAvailable, setBackendAvailable] = useState(null);
  const isAdminPath = location.pathname.startsWith('/admin') || location.pathname.startsWith('/cluster-admin');
  const isInstitutionPath = location.pathname.startsWith('/institution');
  
  const hasIncompleteProfile = user && (!user.name || !user.email || !user.mobile || !user.dob);

  useEffect(() => {
    let isMounted = true;

    const checkBackendHealth = async () => {
      try {
        await axios.get('/api/health', { timeout: 5000 });
        if (isMounted) setBackendAvailable(true);
      } catch (err) {
        if (isMounted) setBackendAvailable(false);
      }
    };

    checkBackendHealth();
    const intervalId = setInterval(checkBackendHealth, config.BACKEND_HEALTH_CHECK_INTERVAL_MS);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, []);

  if (config.MAINTENANCE_MODE || backendAvailable === false) {
    return <MaintenancePage platformName="Seatifyai" supportEmail="support@seatifyai.com" />;
  }

  if (backendAvailable === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background:'var(--bg)'}}>
        <div className="w-8 h-8 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin"/>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col ${user && !isAdminPath ? "pb-24 md:pb-0" : ""}`}>
      {!isAdminPath && !isInstitutionPath && <Navbar />}
      <div className="flex-grow">
        <Routes>
          <Route path="/" element={hasIncompleteProfile ? <Navigate to="/profile-setup" /> : (user?.role === 'admin' ? <Navigate to="/admin" /> : user?.role === 'cluster_admin' ? <Navigate to="/cluster-admin" /> : user?.role === 'institution_admin' ? <Navigate to="/institution" /> : <CoursesPage />)} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/courses" element={hasIncompleteProfile ? <Navigate to="/profile-setup" /> : (user?.role === 'admin' ? <Navigate to="/admin" /> : user?.role === 'cluster_admin' ? <Navigate to="/cluster-admin" /> : user?.role === 'institution_admin' ? <Navigate to="/institution" /> : <CoursesPage />)} />
          <Route path="/:collegeSlug/courses" element={hasIncompleteProfile ? <Navigate to="/profile-setup" /> : (user?.role === 'admin' ? <Navigate to="/admin" /> : user?.role === 'cluster_admin' ? <Navigate to="/cluster-admin" /> : user?.role === 'institution_admin' ? <Navigate to="/institution" /> : <CoursesPage />)} />
          <Route path="/apply/:courseId" element={<ProtectedRoute><ApplicationForm /></ProtectedRoute>} />
          <Route path="/payment/:applicationId" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
          <Route path="/confirmation/:applicationId" element={<ProtectedRoute><ConfirmationPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/admissions" element={<ProtectedRoute><AdmissionsPage /></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
          <Route path="/profile-setup" element={<ProfileSetupRoute><ProfileSetupPage /></ProfileSetupRoute>} />
          <Route path="/cancel-booking/:applicationId" element={<ProtectedRoute><CancelPage /></ProtectedRoute>} />
          
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/receipt/:applicationId" element={<DigitalReceiptPage />} />
          
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<PermissionRoute permission={PERMISSIONS.DASHBOARD_VIEW}><DashboardOverview /></PermissionRoute>} />
            <Route path="admissions" element={<PermissionRoute permission={PERMISSIONS.ADMISSIONS_VIEW}><AdminAdmissions /></PermissionRoute>} />
            <Route path="admissions/:applicationId" element={<PermissionRoute permission={PERMISSIONS.ADMISSIONS_VIEW}><AdminAdmissions /></PermissionRoute>} />
            <Route path="colleges" element={<PermissionRoute permission={PERMISSIONS.INSTITUTIONS_VIEW}><AdminColleges /></PermissionRoute>} />
            <Route path="colleges/:collegeName" element={<PermissionRoute permission={PERMISSIONS.INSTITUTIONS_VIEW}><AdminCollegeDetails /></PermissionRoute>} />
            <Route path="colleges/:collegeName/erp" element={<PermissionRoute permission={PERMISSIONS.COURSES_VIEW}><ErpIntegrationsPage scope="admin" /></PermissionRoute>} />
            <Route path="linked-accounts" element={<PermissionRoute permission={PERMISSIONS.LINKED_ACCOUNTS_VIEW}><AdminLinkedAccounts /></PermissionRoute>} />
            <Route path="students" element={<PermissionRoute permission={PERMISSIONS.STUDENTS_VIEW}><AdminStudents /></PermissionRoute>} />
            <Route path="settlements" element={<PermissionRoute permission={PERMISSIONS.SETTLEMENTS_VIEW}><AdminSettlements /></PermissionRoute>} />
            <Route path="logs" element={<PermissionRoute permission={PERMISSIONS.LOGS_VIEW}><AdminLogs /></PermissionRoute>} />
            <Route path="user-management" element={<PermissionRoute permission={[PERMISSIONS.USER_MANAGEMENT_VIEW, PERMISSIONS.ADMINS_MANAGE]}><AdminUserManagement /></PermissionRoute>} />
            <Route path="user-management/:userId" element={<PermissionRoute permission={PERMISSIONS.ADMINS_MANAGE}><AdminUserManagement editMode /></PermissionRoute>} />
            <Route path="settings" element={<PermissionRoute permission={PERMISSIONS.SETTINGS_VIEW}><AdminSettings /></PermissionRoute>} />
            <Route path="support" element={<PermissionRoute permission={PERMISSIONS.SUPPORT_VIEW}><SupportPage /></PermissionRoute>} />
            <Route path="profile" element={<AdminProfile />} />
            <Route path="*" element={<div className="p-8 text-center text-slate-500">Module coming soon...</div>} />
          </Route>
          <Route path="/cluster-admin" element={<ClusterAdminRoute><AdminLayout /></ClusterAdminRoute>}>
            <Route index element={<PermissionRoute permission={PERMISSIONS.DASHBOARD_VIEW}><DashboardOverview scope="cluster" /></PermissionRoute>} />
            <Route path="admissions" element={<PermissionRoute permission={PERMISSIONS.ADMISSIONS_VIEW}><AdminAdmissions /></PermissionRoute>} />
            <Route path="admissions/:applicationId" element={<PermissionRoute permission={PERMISSIONS.ADMISSIONS_VIEW}><AdminAdmissions /></PermissionRoute>} />
            <Route path="colleges" element={<PermissionRoute permission={PERMISSIONS.INSTITUTIONS_VIEW}><AdminColleges /></PermissionRoute>} />
            <Route path="colleges/:collegeName" element={<PermissionRoute permission={PERMISSIONS.COURSES_VIEW}><AdminCollegeDetails /></PermissionRoute>} />
            <Route path="colleges/:collegeName/erp" element={<PermissionRoute permission={PERMISSIONS.COURSES_VIEW}><ErpIntegrationsPage scope="cluster" /></PermissionRoute>} />
            <Route path="course-management" element={<PermissionRoute permission={PERMISSIONS.COURSE_MANAGEMENT_VIEW}><CourseManagement /></PermissionRoute>} />
            <Route path="course-management/:courseId/students" element={<PermissionRoute permission={PERMISSIONS.COURSE_MANAGEMENT_VIEW}><CourseManagement /></PermissionRoute>} />
            <Route path="students" element={<PermissionRoute permission={PERMISSIONS.STUDENTS_VIEW}><AdminStudents /></PermissionRoute>} />
            <Route path="settlements" element={<PermissionRoute permission={PERMISSIONS.SETTLEMENTS_VIEW}><AdminSettlements /></PermissionRoute>} />
            <Route path="logs" element={<PermissionRoute permission={PERMISSIONS.LOGS_VIEW}><AdminLogs /></PermissionRoute>} />
            <Route path="user-management" element={<PermissionRoute permission={[PERMISSIONS.USER_MANAGEMENT_VIEW, PERMISSIONS.ADMINS_MANAGE]}><AdminUserManagement /></PermissionRoute>} />
            <Route path="user-management/:userId" element={<PermissionRoute permission={PERMISSIONS.ADMINS_MANAGE}><AdminUserManagement editMode /></PermissionRoute>} />
            <Route path="settings" element={<PermissionRoute permission={PERMISSIONS.SETTINGS_VIEW}><AdminSettings /></PermissionRoute>} />
            <Route path="profile" element={<AdminProfile />} />
            <Route path="*" element={<div className="p-8 text-center text-slate-500">Module coming soon...</div>} />
          </Route>
          <Route path="/institution" element={<InstitutionAdminRoute><AdminLayout /></InstitutionAdminRoute>}>
            <Route index element={<PermissionRoute permission={PERMISSIONS.DASHBOARD_VIEW}><DashboardOverview scope="institution" /></PermissionRoute>} />
            <Route path="admissions" element={<PermissionRoute permission={PERMISSIONS.ADMISSIONS_VIEW}><AdminAdmissions apiBase="/api/institution-admin" /></PermissionRoute>} />
            <Route path="admissions/:applicationId" element={<PermissionRoute permission={PERMISSIONS.ADMISSIONS_VIEW}><AdminAdmissions apiBase="/api/institution-admin" /></PermissionRoute>} />
            <Route path="courses" element={<PermissionRoute permission={PERMISSIONS.COURSES_VIEW}><AdminCollegeDetails scope="institution" /></PermissionRoute>} />
            <Route path="course-management" element={<PermissionRoute permission={PERMISSIONS.COURSE_MANAGEMENT_VIEW}><CourseManagement /></PermissionRoute>} />
            <Route path="course-management/:courseId/students" element={<PermissionRoute permission={PERMISSIONS.COURSE_MANAGEMENT_VIEW}><CourseManagement /></PermissionRoute>} />
            <Route path="students" element={<PermissionRoute permission={PERMISSIONS.STUDENTS_VIEW}><AdminStudents apiBase="/api/institution-admin" institutionScope /></PermissionRoute>} />
            <Route path="erp" element={<PermissionRoute permission={PERMISSIONS.COURSES_VIEW}><ErpIntegrationsPage scope="institution" /></PermissionRoute>} />
            <Route path="settlements" element={<PermissionRoute permission={PERMISSIONS.SETTLEMENTS_VIEW}><AdminSettlements apiBase="/api/institution-admin" /></PermissionRoute>} />
            <Route path="logs" element={<PermissionRoute permission={PERMISSIONS.LOGS_VIEW}><AdminLogs /></PermissionRoute>} />
            <Route path="user-management" element={<PermissionRoute permission={[PERMISSIONS.USER_MANAGEMENT_VIEW, PERMISSIONS.ADMINS_MANAGE]}><AdminUserManagement apiBase="/api/institution-admin" institutionScope /></PermissionRoute>} />
            <Route path="user-management/:userId" element={<PermissionRoute permission={PERMISSIONS.ADMINS_MANAGE}><AdminUserManagement apiBase="/api/institution-admin" institutionScope editMode /></PermissionRoute>} />
            <Route path="settings" element={<Navigate to="/institution/user-management" replace />} />
            <Route path="profile" element={<AdminProfile />} />
            <Route path="*" element={<div className="p-8 text-center text-slate-500">Module coming soon...</div>} />
          </Route>
        </Routes>
      </div>
      
      {!isAdminPath && !isInstitutionPath && <Footer />}
      {!isAdminPath && !isInstitutionPath && <WhatsAppButton />}
      {!isAdminPath && !isInstitutionPath && <SupportButton />}
    </div>
  );
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
	        <ScrollToTop />
	        <ClearAdmissionsFiltersOnRouteLeave />
	        <EnforceTextEntryWordLimits />
	        <Toaster
          position="top-right"
          toastOptions={{
            style: { background: '#fff', color: '#1F2937', border: '1px solid var(--card-border)' },
            success: { iconTheme: { primary: '#10B981', secondary: '#fff' } },
            error: { iconTheme: { primary: '#EF4444', secondary: '#fff' } },
            duration: 4000
          }}
        />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
